// Roles and permissions. This file is the only place the role → permission mapping lives.
// Cloud Functions write the computed `perms` and `announceSections` onto events/{e}/staff/{uid};
// firestore.rules checks those arrays. If you change a mapping here, run
// `npm run staff:recompute` so existing staff documents pick it up.

import type { SectionKey, SectionScope } from './types.ts'
import { SECTION_KEYS } from './types.ts'

export const PERMISSIONS = [
  'scores.enter',
  'scores.anyCourt',
  'scores.correct',
  'schedule.edit',
  'schedule.publish',
  'announce.send',
  'teams.manage',
  'registrations.view',
  'payments.view',
  'checkin.manage',
  'vendors.manage',
  'sponsors.manage',
  'quran.manage',
  'import.run',
  'event.configure',
  'content.edit',
  'staff.manage',
  'audit.view',
] as const

export type Permission = (typeof PERMISSIONS)[number]

export const ROLES = ['admin', 'scheduler', 'scorekeeper', 'secretary', 'treasurer', 'announcer', 'quran'] as const
export type Role = (typeof ROLES)[number]

export interface RoleInfo {
  label: string
  description: string
  perms: readonly Permission[]
  /** Sections this role may announce to (only meaningful when perms include announce.send). `chosen` means the per-person announcerSections. */
  sections: readonly SectionScope[] | 'chosen'
  /** The role only makes sense with at least one court assigned (courtKeepers). */
  needsCourts: boolean
}

export const ROLE_INFO: Record<Role, RoleInfo> = {
  admin: {
    label: 'Administrator',
    description: 'Everything, including users, imports, event settings and sponsor placements',
    perms: PERMISSIONS,
    sections: ['all'],
    needsCourts: false,
  },
  scheduler: {
    label: 'Scheduler',
    description: 'Edit and publish the schedule, correct scores on any court, run check-in',
    perms: [
      'schedule.edit',
      'schedule.publish',
      'scores.enter',
      'scores.anyCourt',
      'scores.correct',
      'teams.manage',
      'registrations.view',
      'checkin.manage',
      'announce.send',
    ],
    sections: ['basketball', 'volleyball'],
    needsCourts: false,
  },
  scorekeeper: {
    label: 'Scorekeeper',
    description: 'Enter live scores for their assigned courts only',
    perms: ['scores.enter'],
    sections: [],
    needsCourts: true,
  },
  secretary: {
    label: 'Secretary',
    description: 'Announcements, check-in, team profiles, vendors and the Quran competition',
    perms: ['announce.send', 'teams.manage', 'registrations.view', 'checkin.manage', 'vendors.manage', 'quran.manage', 'content.edit'],
    sections: ['all'],
    needsCourts: false,
  },
  treasurer: {
    label: 'Treasurer',
    description: 'Payment status for teams, vendors and sponsors; vendor and sponsor records',
    perms: ['payments.view', 'registrations.view', 'vendors.manage', 'sponsors.manage'],
    sections: [],
    needsCourts: false,
  },
  announcer: {
    label: 'Announcer',
    description: 'Send announcements to the sections they are assigned',
    perms: ['announce.send'],
    sections: 'chosen',
    needsCourts: false,
  },
  quran: {
    label: 'Quran coordinator',
    description: 'Run the Quran competition order, now reciting and results',
    perms: ['quran.manage', 'announce.send'],
    sections: ['quran'],
    needsCourts: false,
  },
}

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
}

/** Union of permissions for a set of roles, in PERMISSIONS order. */
export function computePermissions(roles: readonly Role[]): Permission[] {
  const granted = new Set<Permission>()
  for (const role of roles) for (const p of ROLE_INFO[role].perms) granted.add(p)
  return PERMISSIONS.filter((p) => granted.has(p))
}

/**
 * Effective announcement scope. Only roles that grant announce.send contribute, so an unscoped role
 * (Treasurer, Scorekeeper) never widens it. Returns ['all'] when any contributing role grants all
 * sections or when the union covers every section.
 */
export function computeAnnounceSections(roles: readonly Role[], announcerSections: readonly SectionKey[]): SectionScope[] {
  const out = new Set<SectionKey>()
  for (const role of roles) {
    const info = ROLE_INFO[role]
    if (!info.perms.includes('announce.send')) continue
    const list = info.sections === 'chosen' ? announcerSections : info.sections
    for (const item of list) {
      if (item === 'all') return ['all']
      out.add(item)
    }
  }
  if (SECTION_KEYS.every((k) => out.has(k))) return ['all']
  return SECTION_KEYS.filter((k) => out.has(k))
}

/** Minimal view of a person's access, from events/{e}/staff/{uid} (or synthesized for platform admins). */
export interface Access {
  perms: readonly Permission[]
  active: boolean
  announceSections: readonly SectionScope[]
  roles?: readonly Role[]
  accessUntil?: { toMillis(): number } | null
  platformAdmin?: boolean
}

/** Access for a platform administrator: every permission, every section. */
export function platformAdminAccess(): Access {
  return { perms: PERMISSIONS, active: true, announceSections: ['all'], roles: ['admin'], accessUntil: null, platformAdmin: true }
}

export function isAccessCurrent(access: Access | null | undefined, now: number = Date.now()): boolean {
  if (!access || !access.active) return false
  if (access.accessUntil && access.accessUntil.toMillis() <= now) return false
  return true
}

export function can(access: Access | null | undefined, perm: Permission, now?: number): boolean {
  return isAccessCurrent(access, now) && access!.perms.includes(perm)
}

export function canAny(access: Access | null | undefined, perms: readonly Permission[], now?: number): boolean {
  return perms.some((p) => can(access, p, now))
}

export function sectionsCovered(access: Access | null | undefined, sections: readonly SectionKey[]): boolean {
  const scope = access?.announceSections ?? []
  if (scope.includes('all')) return true
  return sections.every((s) => (scope as readonly string[]).includes(s))
}

/** Mirrors the announcements create rule. */
export function canAnnounceTo(access: Access | null | undefined, audience: { type: string; sections: readonly SectionKey[] }): boolean {
  if (!can(access, 'announce.send')) return false
  const scope = access!.announceSections
  if (scope.includes('all')) return true
  if (audience.type === 'all') return false
  return audience.sections.length > 0 && sectionsCovered(access, audience.sections)
}

/**
 * Who may retry a failed announcement: its sender or staff.manage; for automatic ones, anyone
 * who can announce to its (stored) audience. announcementRetry and the admin site both use this.
 */
export function canRetryAnnouncement(
  uid: string,
  access: Access | null | undefined,
  a: { createdBy: string; automatic?: boolean; audience?: { type: string; sections: readonly SectionKey[] } | null },
): boolean {
  if (!can(access, 'announce.send')) return false
  if (a.createdBy === uid || can(access, 'staff.manage')) return true
  if (a.automatic !== true || !a.audience || !Array.isArray(a.audience.sections)) return false
  return canAnnounceTo(access, { type: a.audience.type, sections: a.audience.sections })
}

/** Sections an announcer may pick from. */
export function announceableSections(access: Access | null | undefined): SectionKey[] {
  if (!can(access, 'announce.send')) return []
  const scope = access!.announceSections
  if (scope.includes('all')) return [...SECTION_KEYS]
  return SECTION_KEYS.filter((k) => (scope as readonly string[]).includes(k))
}

/** Can this person score a game on this court right now? `keeperUids` comes from courtKeepers/{courtId}. */
export function canScoreCourt(access: Access | null | undefined, uid: string, keeperUids: readonly string[]): boolean {
  if (!can(access, 'scores.enter')) return false
  return can(access, 'scores.anyCourt') || keeperUids.includes(uid)
}

/** Where to send someone after sign-in. */
export function landingPath(access: Access | null | undefined): string {
  if (!isAccessCurrent(access)) return '/login'
  const perms = access!.perms
  const scoreOnly = perms.length > 0 && perms.every((p) => p === 'scores.enter')
  if (scoreOnly) return '/score/'
  const quranOnly = perms.includes('quran.manage') && perms.every((p) => p === 'quran.manage' || p === 'announce.send')
  if (quranOnly) return '/quran'
  if (perms.includes('scores.enter') || perms.includes('schedule.edit')) return '/live'
  if (perms.includes('vendors.manage')) return '/vendors'
  if (perms.includes('teams.manage')) return '/teams'
  return '/home'
}

/** Permissions whose data should not be cached on a device (the main admin bundle uses memory cache). */
export const SENSITIVE_PERMS: readonly Permission[] = ['registrations.view', 'payments.view', 'staff.manage', 'import.run', 'quran.manage']

export function roleLabels(roles: readonly Role[]): string {
  return roles.map((r) => ROLE_INFO[r].label).join(', ')
}
